import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    TextField,
    Button,
    Grid,
    Typography,
    Card,
    CardContent,
    CardActions
} from '@mui/material';
import dayjs from 'dayjs';
import { DataGrid } from '@mui/x-data-grid';
import { t } from 'i18next';
import { useUsers } from '../../../hooks/useUsers';
import { createGroup, updateGroupRoles } from '../../../services/groups';

const CreateGroup = () => {
    const navigate = useNavigate();

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [startDate, setStartDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(dayjs().add(30, 'day').format('YYYY-MM-DD'));

    const [selectedPatientUUIDs, setSelectedPatientUUIDs] = useState([]);
    const [search, setSearch] = useState('');

    const {
        data: patientRows = [],
        isLoading,
        paginationModel,
        setPaginationModel,
        count,
        rowsPerPageOptions
    } = useUsers(['PATIENT']);

    const handleCreateGroup = async () => {
        const newGroup = {
            name,
            description,
            start_date: startDate,
            end_date: endDate
        };

        try {
            const createdGroup = await createGroup(newGroup);
            console.log('Grupo criado com sucesso:', createdGroup);

            if (selectedPatientUUIDs.length > 0) {
                await updateGroupRoles({
                    uuid: createdGroup.uuid,
                    to_add: selectedPatientUUIDs,
                    to_remove: []
                });
                console.log('Pacientes adicionados ao grupo.');
            }

            navigate('/grupos');
        } catch (error) {
            console.error('Erro ao criar grupo ou adicionar pacientes:', error);
        }
    };

    const filteredRows = patientRows.filter((p) =>
        p.name.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <Card sx={{ m: 2 }}>
            <CardContent>
                <Typography variant="h4" gutterBottom>
                    {t('groups.create_new_group')}
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('groups.name_label')}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('groups.description_label')}
                            multiline
                            rows={3}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            type="date"
                            label={t('groups.start_date_label')}
                            InputLabelProps={{ shrink: true }}
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            type="date"
                            label={t('groups.end_date_label')}
                            InputLabelProps={{ shrink: true }}
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </Grid>
                </Grid>

                <TextField
                    label={t('groups.search_patient_label')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                />

                <Typography variant="h6" gutterBottom>
                    {t('groups.select_patients')}
                </Typography>

                <DataGrid
                    autoHeight
                    checkboxSelection
                    disableRowSelectionOnClick
                    disableColumnMenu
                    loading={isLoading}
                    rows={filteredRows}
                    getRowId={(row) => row.role_uuid}
                    columns={[{ field: 'name', headerName: 'Nome', flex: 1 }]}
                    rowSelectionModel={filteredRows
                        .filter((row) => selectedPatientUUIDs.includes(row.role_uuid))
                        .map((row) => row.role_uuid)}
                    onRowSelectionModelChange={(selectionModel) => {
                        const currentPageUUIDs = filteredRows.map((row) => row.role_uuid);

                        const newSelected = [
                            ...selectedPatientUUIDs.filter(
                                (id) => !currentPageUUIDs.includes(id)
                            ),
                            ...selectionModel
                        ];

                        setSelectedPatientUUIDs([...new Set(newSelected)]);
                    }}
                    paginationMode="server"
                    rowCount={count}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={rowsPerPageOptions}
                />
            </CardContent>

            <CardActions sx={{ justifyContent: 'space-between', p: 2 }}>
                <Button variant="outlined" onClick={() => navigate('/grupos')}>
                    {t('groups.cancel')}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCreateGroup}
                    disabled={!name || selectedPatientUUIDs.length === 0}
                >
                    {t('groups.create_group')}
                </Button>
            </CardActions>
        </Card>
    );
};

export default CreateGroup;
